import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
  RefreshControl,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { supabase } from '../services/supabase';
import {
  getEventById,
  getEventCheckIns,
  generateEventCheckInCode,
  updateEventCheckInMode,
  getLocationBasedAttendees,
} from '../services/database';

const isWeb = Platform.OS === 'web';
let QRCodeDisplay;
if (isWeb) {
  QRCodeDisplay = require('qrcode.react').QRCodeSVG;
} else {
  QRCodeDisplay = require('react-native-qrcode-svg').default;
}

const ROTATE_INTERVAL_MS = 30000;

const EventCheckInScreen = ({ navigation, route }) => {
  const { eventId } = route.params || {};
  const insets = useSafeAreaInsets();
  const [event, setEvent] = useState(null);
  const [checkIns, setCheckIns] = useState([]);
  const [checkInCode, setCheckInCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [locationEstimating, setLocationEstimating] = useState(false);

  const loadEvent = useCallback(async () => {
    if (!eventId) return;
    try {
      const data = await getEventById(eventId);
      setEvent(data);
      if (data.check_in_mode === 'QR_CODE' && data.check_in_code) {
        setCheckInCode(data.check_in_code);
      }
    } catch (e) {
      console.error('EventCheckIn load event:', e);
    }
  }, [eventId]);

  const loadCheckIns = useCallback(async () => {
    if (!eventId) return;
    try {
      const list = await getEventCheckIns(eventId);
      setCheckIns(list);
    } catch (e) {
      console.error('EventCheckIn load check-ins:', e);
    }
  }, [eventId]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([loadEvent(), loadCheckIns()]);
    setRefreshing(false);
  }, [loadEvent, loadCheckIns]);

  useEffect(() => {
    if (!eventId) return;
    (async () => {
      setLoading(true);
      await loadEvent();
      await loadCheckIns();
      setLoading(false);
    })();
  }, [eventId, loadEvent, loadCheckIns]);

  // Generate or refresh QR code when in QR_CODE mode
  useEffect(() => {
    if (!eventId || !event || event.check_in_mode !== 'QR_CODE') return;
    let cancelled = false;
    const generate = async () => {
      try {
        const { check_in_code } = await generateEventCheckInCode(eventId);
        if (!cancelled) setCheckInCode(check_in_code);
      } catch (e) {
        console.error('Generate check-in code:', e);
      }
    };
    generate();
    const interval = setInterval(generate, ROTATE_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [eventId, event?.check_in_mode]);

  // Realtime subscription for event_check_ins
  useEffect(() => {
    if (!eventId) return;
    const channel = supabase
      .channel(`event-check-ins-${eventId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'event_check_ins',
          filter: `event_id=eq.${eventId}`,
        },
        () => {
          loadCheckIns();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [eventId, loadCheckIns]);

  const handleSwitchToLocationMode = async () => {
    try {
      await updateEventCheckInMode(eventId, 'LOCATION');
      await loadEvent();
    } catch (e) {
      console.error('Switch to location mode:', e);
    }
  };

  const LOCATION_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

  const refreshLocationEstimate = useCallback(async () => {
    if (!eventId) return;
    try {
      await getLocationBasedAttendees(eventId, null, true);
      await loadCheckIns();
    } catch (e) {
      console.error('Location estimate:', e);
    }
  }, [eventId, loadCheckIns]);

  const handleRefreshLocationEstimate = async () => {
    if (!eventId) return;
    setLocationEstimating(true);
    await refreshLocationEstimate();
    setLocationEstimating(false);
  };

  // Periodic location-based attendance sync when in LOCATION mode
  useEffect(() => {
    if (!eventId || event?.check_in_mode !== 'LOCATION') return;
    refreshLocationEstimate();
    const interval = setInterval(refreshLocationEstimate, LOCATION_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [eventId, event?.check_in_mode, refreshLocationEstimate]);

  if (loading && !event) {
    return (
      <View style={[styles.container, { paddingTop: insets.top }]}>
        <Text style={styles.loadingText}>Loading…</Text>
      </View>
    );
  }

  if (!event) {
    return (
      <View style={[styles.container, { paddingTop: insets.top }]}>
        <Text style={styles.errorText}>Event not found</Text>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
          <Text style={styles.backBtnText}>Back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const isQRMode = event.check_in_mode === 'QR_CODE';
  const isLocationMode = event.check_in_mode === 'LOCATION';
  const qrValue = checkInCode
    ? `chessmaps://check-in/${eventId}?code=${encodeURIComponent(checkInCode)}`
    : '';

  const content = (
    <>
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.headerBack}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <Icon name="arrow-back" size={24} color="#333" />
          <Text style={styles.headerBackLabel}>Back</Text>
        </TouchableOpacity>
        {!fullScreen && (
          <TouchableOpacity
            onPress={() => setFullScreen(true)}
            style={styles.fullScreenBtn}
          >
            <Icon name="fullscreen" size={24} color="#333" />
          </TouchableOpacity>
        )}
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={refresh} />
        }
      >
        <Text style={styles.title}>{event.title}</Text>
        <Text style={styles.attendeeCount}>Checked in: {checkIns.length}</Text>

        {isQRMode && (
          <>
            <View style={styles.qrWrap}>
              {qrValue ? (
                isWeb ? (
                  <QRCodeDisplay value={qrValue} size={220} level="M" />
                ) : (
                  <QRCodeDisplay value={qrValue} size={220} />
                )
              ) : (
                <Text style={styles.qrPlaceholder}>Generating code…</Text>
              )}
            </View>
            <Text style={styles.qrHint}>Players scan this QR code in the app to check in.</Text>
            {checkInCode && (
              <Text style={styles.qrCodeText}>
                Or share this code for web check-in: <Text style={styles.qrCodeBold}>{checkInCode}</Text>
              </Text>
            )}
            <TouchableOpacity
              style={styles.modeSwitch}
              onPress={handleSwitchToLocationMode}
            >
              <Icon name="location-on" size={20} color="#666" />
              <Text style={styles.modeSwitchText}>Use location-based turnout instead</Text>
            </TouchableOpacity>
          </>
        )}

        {isLocationMode && (
          <>
            <Text style={styles.locationNote}>
              Turnout is estimated using nearby player locations (less precise than QR check-in).
            </Text>
            <TouchableOpacity
              style={[styles.refreshLocationBtn, locationEstimating && styles.buttonDisabled]}
              onPress={handleRefreshLocationEstimate}
              disabled={locationEstimating}
            >
              <Text style={styles.refreshLocationText}>
                {locationEstimating ? 'Updating…' : 'Refresh location estimate'}
              </Text>
            </TouchableOpacity>
          </>
        )}

        <View style={styles.listSection}>
          <Text style={styles.listTitle}>Attendees ({checkIns.length})</Text>
          {checkIns.length === 0 ? (
            <Text style={styles.emptyList}>No check-ins yet</Text>
          ) : (
            checkIns.map((row) => {
              const u = row.user || {};
              return (
                <View key={row.id} style={styles.checkInRow}>
                  <Text style={styles.checkInName}>{u.username || 'Guest'}</Text>
                  <Text style={styles.checkInMethod}>{row.check_in_method}</Text>
                  <Text style={styles.checkInTime}>
                    {row.checked_in_at
                      ? new Date(row.checked_in_at).toLocaleTimeString(undefined, {
                          hour: 'numeric',
                          minute: '2-digit',
                        })
                      : ''}
                  </Text>
                </View>
              );
            })
          )}
        </View>
      </ScrollView>
    </>
  );

  if (fullScreen) {
    return (
      <View style={[styles.fullScreenContainer, { paddingTop: insets.top }]}>
        {content}
        <TouchableOpacity
          style={styles.exitFullScreen}
          onPress={() => setFullScreen(false)}
        >
          <Icon name="fullscreen-exit" size={28} color="#333" />
          <Text style={styles.exitFullScreenText}>Exit full screen</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      {content}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  fullScreenContainer: {
    flex: 1,
    backgroundColor: '#fff',
  },
  loadingText: {
    fontSize: 18,
    color: '#666',
    textAlign: 'center',
    marginTop: 40,
  },
  errorText: {
    fontSize: 18,
    color: '#666',
    textAlign: 'center',
    marginTop: 40,
  },
  backBtn: {
    marginTop: 16,
    alignSelf: 'center',
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: '#eee',
    borderRadius: 8,
  },
  backBtnText: {
    fontSize: 16,
    color: '#333',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  headerBack: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerBackLabel: {
    fontSize: 16,
    color: '#333',
    marginLeft: 4,
  },
  fullScreenBtn: {
    padding: 8,
  },
  exitFullScreen: {
    position: 'absolute',
    bottom: 24,
    right: 24,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: '#f0f0f0',
    borderRadius: 8,
  },
  exitFullScreenText: {
    fontSize: 16,
    color: '#333',
    marginLeft: 8,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 80,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#333',
    marginBottom: 8,
  },
  attendeeCount: {
    fontSize: 16,
    color: '#666',
    marginBottom: 24,
  },
  qrWrap: {
    alignSelf: 'center',
    width: 240,
    height: 240,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 12,
    marginBottom: 12,
    ...Platform.select({
      web: {},
      default: { borderWidth: 1, borderColor: '#e0e0e0' },
    }),
  },
  qrPlaceholder: {
    fontSize: 16,
    color: '#999',
  },
  qrHint: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginBottom: 8,
  },
  qrCodeText: {
    fontSize: 13,
    color: '#666',
    textAlign: 'center',
    marginBottom: 20,
  },
  qrCodeBold: {
    fontWeight: '700',
    color: '#333',
  },
  modeSwitch: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    marginBottom: 24,
  },
  modeSwitchText: {
    fontSize: 15,
    color: '#666',
    marginLeft: 8,
  },
  locationNote: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginBottom: 16,
  },
  refreshLocationBtn: {
    alignSelf: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    backgroundColor: '#e0e0e0',
    borderRadius: 8,
    marginBottom: 24,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  refreshLocationText: {
    fontSize: 15,
    color: '#333',
  },
  listSection: {
    marginTop: 8,
  },
  listTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
  },
  emptyList: {
    fontSize: 15,
    color: '#999',
  },
  checkInRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  checkInName: {
    flex: 1,
    fontSize: 16,
    color: '#333',
  },
  checkInMethod: {
    fontSize: 12,
    color: '#888',
    marginRight: 8,
    textTransform: 'uppercase',
  },
  checkInTime: {
    fontSize: 13,
    color: '#666',
  },
});

export default EventCheckInScreen;
